import { X } from "lucide-react";
import { useEffect, useRef, type HTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { cn } from "../../lib/cn";

export const Panel = ({ className, ...p }: HTMLAttributes<HTMLDivElement>) => <section className={cn("rounded-card border border-ln bg-sf p-5", className)} {...p} />;
export const H2 = ({ className, ...p }: HTMLAttributes<HTMLHeadingElement>) => <h2 className={cn("m-0 mb-3.5 font-display text-[18px] font-medium tracking-[-0.02em] text-tx", className)} {...p} />;
export const Eyebrow = ({ children }: { children: ReactNode }) => <div className="font-mono text-[12px] font-bold uppercase tracking-[0.18em] text-ok">{children}</div>;
export const Label = ({ className, ...p }: HTMLAttributes<HTMLDivElement>) => <div className={cn("font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-mu", className)} {...p} />;
export const Mono = ({ className, ...p }: HTMLAttributes<HTMLSpanElement>) => <span className={cn("font-mono", className)} {...p} />;

export function Chip({ children, tone = "neutral", className, title }: { children: ReactNode; tone?: "neutral" | "good" | "warn" | "bad" | "info" | "accent"; className?: string; title?: string }) {
  const t = { neutral: "border-ln2 text-tx", good: "border-gd/50 text-gd", warn: "border-wn/60 text-wn", bad: "border-er/60 text-er", info: "border-in/50 text-in", accent: "border-ac/50 text-ok" }[tone];
  return <span title={title} className={cn("inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-lg border px-2.5 text-[13px]", t, className)}>{children}</span>;
}

export const Dot = ({ color, pulse, className }: { color: string; pulse?: boolean; className?: string }) => (
  <span className={cn("inline-block h-2 w-2 shrink-0 rounded-full", pulse && "pulse", className)} style={{ background: `var(--${color})` }} aria-hidden />
);

const field = "h-11 w-full rounded-card border border-ln2 bg-bg px-3 text-[15px] text-tx placeholder:text-mu/70";
export const Input = ({ className, ...p }: InputHTMLAttributes<HTMLInputElement>) => <input className={cn(field, className)} {...p} />;
export const Textarea = ({ className, ...p }: TextareaHTMLAttributes<HTMLTextAreaElement>) => <textarea className={cn(field, "h-auto resize-y px-3 py-2.5", className)} {...p} />;
export const Select = ({ className, ...p }: SelectHTMLAttributes<HTMLSelectElement>) => <select className={cn(field, "cursor-pointer pr-8 font-medium text-[14px]", className)} {...p} />;
export const FilterSelect = ({ className, ...p }: SelectHTMLAttributes<HTMLSelectElement>) => <select className={cn("h-11 cursor-pointer rounded-[10px] border border-ln2 bg-sf px-3.5 text-[14px] font-medium text-tx", className)} {...p} />;
export const Field = ({ label, children, className }: { label: string; children: ReactNode; className?: string }) => (
  <label className={cn("flex flex-col gap-1.5", className)}><Label>{label}</Label>{children}</label>
);

export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled} onClick={() => onChange(!checked)}
      className={cn("relative h-7 w-12 shrink-0 cursor-pointer rounded-full border transition-colors disabled:opacity-50", checked ? "border-ac bg-ac" : "border-ln2 bg-rs")}>
      <span className={cn("absolute top-0.5 h-5 w-5 rounded-full transition-all", checked ? "left-6 bg-acx" : "left-0.5 bg-mu")} />
    </button>
  );
}

export function Progress({ value, tone = "ac" }: { value: number; tone?: "ac" | "wn" | "er" | "gd" }) {
  return <div className="h-1 overflow-hidden rounded bg-rs" role="progressbar" aria-valuenow={Math.round(value)} aria-valuemin={0} aria-valuemax={100}><div className="h-full rounded" style={{ width: `${Math.min(100, Math.max(0, value))}%`, background: `var(--${tone})` }} /></div>;
}

/** Diálogo nativo (<dialog>): accesible, foco atrapado; hoja inferior en móvil. */
export function Dialog({ open, onClose, title, children, wide }: { open: boolean; onClose: () => void; title: string; children: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog ref={ref} onClose={onClose} onClick={(e) => { if (e.target === ref.current) onClose(); }}
      className={cn("m-auto max-h-[92dvh] w-full overflow-y-auto rounded-3xl border border-ln2 bg-sf p-0 max-sm:mb-0 max-sm:max-w-none max-sm:rounded-b-none", wide ? "max-w-2xl" : "max-w-[520px]")}>
      {open && (
        <div className="flex flex-col gap-3.5 p-6 slide-up">
          <div className="flex items-center justify-between">
            <h2 className="m-0 font-display text-[22px] font-medium tracking-[-0.02em]">{title}</h2>
            <button onClick={onClose} aria-label="Cerrar" className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-lg text-mu hover:text-tx"><X className="h-5 w-5" /></button>
          </div>
          {children}
        </div>
      )}
    </dialog>
  );
}

export function Details({ title, children, defaultOpen }: { title: string; children: ReactNode; defaultOpen?: boolean }) {
  return (
    <details open={defaultOpen} className="border-t border-ln pt-3">
      <summary className="flex min-h-8 items-center gap-2 text-[14px] font-medium text-tx"><span className="chev inline-block transition-transform">›</span>{title}</summary>
      <div className="mt-2.5">{children}</div>
    </details>
  );
}

export const Empty = ({ children }: { children: ReactNode }) => <div className="px-4 py-5 text-[14px] text-mu">{children}</div>;
export const Skeleton = ({ className }: { className?: string }) => <div className={cn("animate-pulse rounded-card bg-rs", className)} />;
